import axios from "axios";

export class ApiService {
  constructor() {
    this.authToken = null;
    this.endpoints = {
      adminTool: process.env.REACT_APP_ADMIN_TOOL_URL,
      api: process.env.REACT_APP_QR_API_URL,
    };
  }

  get adminToolBaseUrl() {
    return this.endpoints.adminTool;
  }

  get qrApiBaseUrl() {
    return this.endpoints.api;
  }

  setAuthToken(token) {
    this.authToken = token;
  }

  getHeaders(additionalHeaders = {}) {
    const headers = { ...additionalHeaders };

    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    return headers;
  }

  getTenantHeaders(tenant) {
    return this.getHeaders({ "x-company": tenant });
  }

  async fetchTenants() {
    const headers = this.getHeaders();
    try {
      const response = await axios.get(`${this.adminToolBaseUrl}/whoami`, {
        headers,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch tenants:", error);
      throw error;
    }
  }

  async getUserActions(tenant) {
    try {
      const headers = this.getTenantHeaders(tenant);

      const canIUseResponse = await axios.post(
        `${this.adminToolBaseUrl}/canIUse`,
        {
          features: ["dpp-qr-create", "dpp-qr-read", "dpp-qr-update"],
          modules: ["DPP.QR Management"],
        },
        { headers }
      );

      return canIUseResponse?.data?.features || [];
    } catch (error) {
      console.error("Failed to get user actions:", error);
      throw error;
    }
  }

  async getRoleFeatures(tenant, role, headers) {
    try {
      const response = await axios.get(
        `${this.adminToolBaseUrl}/tenants/${tenant}/roles/${role}/platforms/CUBE/solutions/DPP/features`,
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get features for role ${role}:`, error);
      throw error;
    }
  }

  async getUserRoles(username, tenant, headers) {
    try {
      const response = await axios.get(
        `${this.adminToolBaseUrl}/tenants/${tenant}/users/${username}/platforms/CUBE/solutions/DPP/roles`,
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to get user roles:", error);
      throw error;
    }
  }

  async fetchAllItems(tenant) {
    const headers = this.getTenantHeaders(tenant);
    try {
      const response = await axios.get(`${this.qrApiBaseUrl}/qr/items`, {
        headers,
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch items for tenant ${tenant}:`, error);
      throw error;
    }
  }

  async fetchValue(tenant, id) {
    const headers = this.getTenantHeaders(tenant);
    try {
      const encodedId = encodeURIComponent(id);
      const response = await axios.get(
        `${this.qrApiBaseUrl}/qr/items/${encodedId}`,
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch value for key ${id}:`, error);
      throw error;
    }
  }

  async updateValue(tenant, id, newValue) {
    const headers = this.getTenantHeaders(tenant);
    try {
      const response = await axios.put(
        `${this.qrApiBaseUrl}/qr/items/${id}`,
        { rina_id: newValue },
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to update value for key ${id}:`, error);
      throw error;
    }
  }

  async getQrCodeImage(rinaId, tenant) {
    const headers = this.getTenantHeaders(tenant);
    const encodedRinaId = encodeURIComponent(rinaId);
    try {
      const response = await axios.get(
        `${this.qrApiBaseUrl}/qr/image/${encodedRinaId}`,
        {
          headers,
          responseType: "blob",
        }
      );
      return window.URL.createObjectURL(response.data);
    } catch (error) {
      console.error(`Failed to fetch QR image for rina_id ${rinaId}:`, error);
      throw error;
    }
  }

  async createKey(tenant, id, value) {
    const headers = this.getTenantHeaders(tenant);
    try {
      const response = await axios.post(
        `${this.qrApiBaseUrl}/qr/items`,
        {
          id,
          rina_id: value,
        },
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to create key ${id}:`, error);
      throw error;
    }
  }
}

// Create a singleton instance
export const apiService = new ApiService();
